export const MEETING_DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
] as const

export type MeetingDayKey = (typeof MEETING_DAYS)[number]['key']

export type DayWindow = { start: string; end: string }

export type WeekHours = Record<MeetingDayKey, DayWindow[]>

export type MeetingTypeDraft = {
  id: string
  name: string
  duration_minutes: number
  description: string
}

export type MeetingScreen = {
  id: string
  business_id: string
  calendar_sync_id: string | null
  name: string
  slug: string
  enabled: boolean
  headline: string
  intro: string
  host_name: string
  show_logo: boolean
  accent_color: string
  background_color: string
  return_label: string
  return_url: string
  timezone: string
  hours: WeekHours
  meeting_types: MeetingTypeDraft[]
  buffer_minutes: number
  min_notice_hours: number
  window_days: number
  start_interval_minutes: number
  look_busier: boolean
}

export type BusinessHoursInput = Record<string, {
  enabled?: boolean
  closed?: boolean
  open?: string
  close?: string
} | undefined> | null | undefined

const DAY_FROM_BUSINESS: Record<string, MeetingDayKey> = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
}

export function emptyHours(): WeekHours {
  return { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] }
}

export function defaultHours(): WeekHours {
  const weekday = [{ start: '09:00', end: '17:00' }]
  return { mon: weekday, tue: [...weekday], wed: [...weekday], thu: [...weekday], fri: [...weekday], sat: [], sun: [] }
}

export function hoursFromBusiness(input: BusinessHoursInput): WeekHours {
  const hours = emptyHours()
  if (!input) return defaultHours()
  let any = false
  for (const [name, key] of Object.entries(DAY_FROM_BUSINESS)) {
    const day = input[name]
    if (!day || day.enabled === false || day.closed) continue
    const start = String(day.open || '').slice(0, 5)
    const end = String(day.close || '').slice(0, 5)
    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end) || end <= start) continue
    hours[key] = [{ start, end }]
    any = true
  }
  return any ? hours : defaultHours()
}

function mix(text: string) {
  let hash = 2166136261
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/** Keep about 40% of an open day, spread out, so a quiet calendar does not look empty. */
export function thinOpenSlots<T>(slots: T[], seed: string): T[] {
  if (slots.length <= 2) return slots
  const keep = Math.max(2, Math.round(slots.length * 0.4))
  if (keep >= slots.length) return slots
  const shift = mix(seed) % slots.length
  const stride = slots.length / keep
  const chosen = new Set<number>()
  for (let i = 0; i < keep; i++) chosen.add(Math.floor(shift + i * stride) % slots.length)
  let cursor = shift
  while (chosen.size < keep) {
    chosen.add(cursor % slots.length)
    cursor++
  }
  return slots.filter((_, index) => chosen.has(index))
}

export function slugifyScreenName(name: string) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
  return slug || 'meeting'
}

export function newMeetingType(): MeetingTypeDraft {
  return { id: crypto.randomUUID(), name: '30 minute meeting', duration_minutes: 30, description: '' }
}

export function normalizeHours(value: unknown): WeekHours {
  const hours = emptyHours()
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  for (const day of MEETING_DAYS) {
    const windows = Array.isArray(raw[day.key]) ? raw[day.key] as DayWindow[] : []
    hours[day.key] = windows
      .map((w) => ({ start: String(w?.start || '').slice(0, 5), end: String(w?.end || '').slice(0, 5) }))
      .filter((w) => /^\d{2}:\d{2}$/.test(w.start) && /^\d{2}:\d{2}$/.test(w.end))
  }
  return hours
}

export function normalizeTypes(value: unknown): MeetingTypeDraft[] {
  if (!Array.isArray(value)) return []
  return value.map((row) => {
    const item = row as Partial<MeetingTypeDraft>
    return {
      id: String(item.id || crypto.randomUUID()),
      name: String(item.name || '').slice(0, 80),
      duration_minutes: Number(item.duration_minutes) || 30,
      description: String(item.description || '').slice(0, 280),
    }
  }).filter((item) => item.name.trim())
}

export function screenPath(businessSlug: string, screenSlug: string) {
  return `/${businessSlug}/meet/${screenSlug}`
}
